import { Program, User } from '@citruscamps/citrus-client'
import { DateTime, Interval } from 'luxon'
import React, { useState } from 'react'
import { NavLink } from 'react-router-dom'
import { ReferralModal } from '../../../../../components/DashboardLayout/components/ReferralModal'
import { useFetchCitrusPromotion } from '../../../../../components/DashboardLayout/hooks/useFetchCitrusPromotions'
import { ProgressDots } from '../../../../../components/ProgressDots/ProgressDots'
import { ROUTES } from '../../../../../constants/routes'
import { ReactComponent as UndrawBlogging } from '../../../../../images/undraw_blogging_vpvv.svg'
import { ReactComponent as UndrawEvents } from '../../../../../images/undraw_events_2p66.svg'
// import { ReactComponent as UndrawHalloween } from '../../../../../images/undraw_halloween_re_2kq1.svg'
import { ReactComponent as UndrawPayOnline } from '../../../../../images/undraw_pay_online_b1hk.svg'
// import { ReactComponent as UndrawResearching } from '../../../../../images/undraw_researching_22gp.svg'
import { ReactComponent as UndrawReviewedDocs } from '../../../../../images/undraw_reviewed_docs_neeb.svg'
import { ReactComponent as UndrawSelectingTeam } from '../../../../../images/undraw_selecting_team_8uux.svg'
// import { ReactComponent as UndrawSnowGames } from '../../../../../images/undraw_snow_games_ohkc.svg'
import { ReactComponent as UndrawWorkTogether } from '../../../../../images/undraw_work_together_h63l.svg'
import { formatList } from '../../../../../utils/formatters'
import { ExtendedOnboardingStep, OnboardingStep } from '../hooks/useFetchOnboarding'

const DefaultOnboarding: OnboardingStep[] = ['camps', 'branding', 'team', 'payout']

interface HeaderContent {
  title: React.ReactElement
  text: React.ReactElement
  image: React.ReactElement
}

interface IProps {
  user: User
  program: Program
  onboarding: OnboardingStep[]
  isLoading: boolean
  isRecommendedToUpgradePlan: boolean
  isRecommendedToReferral: boolean
  onSkip: (step: ExtendedOnboardingStep) => void
  onSetupPayout: () => void
}

export const DashboardHeader = ({
  user,
  program,
  onboarding,
  isLoading,
  isRecommendedToReferral,
  isRecommendedToUpgradePlan,
  onSkip: handleSkip,
  onSetupPayout: handleSetupPayout,
}: IProps) => {
  const [isReferralModalOpen, setIsReferralModalOpen] = useState<boolean>()
  const { data: citrusPromo } = useFetchCitrusPromotion({ program })
  const { referral } = citrusPromo || {}
  const referalAmount: string | undefined = referral?.display?.split('.').shift()
  const content: HeaderContent = {
    title: <>Welcome back {user?.first_name}!</>,
    text: (
      <>
        Let us make it easy to handle logistics and operations so you can focus on growing your
        sports business
      </>
    ),
    image: <UndrawBlogging className="img-fluid" />,
  }

  const today = DateTime.local()
  const daysLeftInTrial: number = program.billing?.trial_end
    ? Math.round(
        Interval.fromDateTimes(today, DateTime.fromJSDate(program.billing.trial_end)).toDuration(
          'days',
        ).days,
      )
    : 0
  const isPastDue = program.billing?.status === 'past_due'
  const isNearEndOfTrail =
    program.billing?.trial_start &&
    program.billing?.trial_end &&
    DateTime.fromJSDate(program.billing.trial_start) <= today &&
    DateTime.fromJSDate(program.billing.trial_end) >= today &&
    daysLeftInTrial < 7
  const isMissingPaymentMethod = !program.billing?.has_payment_method
  if (isPastDue) {
    content.title = <>Your subscription is past due</>
    content.text = (
      <>
        <p>
          <b>We have failed to process your payment</b> To ensure your youth program isn't
          interupted add a payment method to your account.
        </p>
        <NavLink to={ROUTES.BILLING_SETTINGS} className="btn btn-primary">
          Manage Billing
        </NavLink>
      </>
    )
    content.image = <UndrawReviewedDocs className="img-fluid" />
  } else if (isNearEndOfTrail && isMissingPaymentMethod) {
    content.title = <>Your trial is almost over</>
    content.text = (
      <>
        <p>
          <b>
            Your {formatList(program.billing?.products.map((p) => p.name) || []) || 'plan'} trial
            ends{' '}
            {program.billing?.trial_end
              ? DateTime.fromJSDate(program.billing?.trial_end).toRelative()
              : 'soon'}
            .
          </b>{' '}
          To ensure your youth program isn't interupted add a payment method to your account.
        </p>
        <NavLink to={ROUTES.BILLING_SETTINGS} className="btn btn-primary">
          Manage Billing
        </NavLink>
      </>
    )
    content.image = <UndrawReviewedDocs className="img-fluid" />
  } else if (onboarding.includes('camps')) {
    content.title = <>Schedule a Event</>
    content.text = (
      <>
        <p>
          Create your very first camp using Citrus. Add the logistics and description and we will
          handle the rest.
        </p>
        <div>
          <ProgressDots
            currentStep={DefaultOnboarding.length - onboarding.length}
            totalSteps={Math.max(DefaultOnboarding.length, onboarding.length)}
          />
        </div>
        <NavLink to={ROUTES.EVENT_CREATE} className="btn btn-primary">
          Create Event
        </NavLink>
        <button type="button" className="btn ms-2" onClick={() => handleSkip('camps')}>
          Skip
        </button>
      </>
    )
    content.image = <UndrawEvents className="img-fluid" />
  } else if (onboarding.includes('branding')) {
    content.title = <>Let's add your program's branding</>
    content.text = (
      <>
        <p>Customize your checkout so that your attendees recognize your brand.</p>
        <div>
          <ProgressDots
            currentStep={DefaultOnboarding.length - onboarding.length}
            totalSteps={Math.max(DefaultOnboarding.length, onboarding.length)}
          />
        </div>
        <NavLink to={ROUTES.BRANDING_SETTINGS} className="btn btn-primary">
          Customize Branding
        </NavLink>
        <button type="button" className="btn ms-2" onClick={() => handleSkip('branding')}>
          Skip
        </button>
      </>
    )
    content.image = <UndrawWorkTogether className="img-fluid" />
  } else if (onboarding.includes('team')) {
    content.title = <>Invite your team</>
    content.text = (
      <>
        <p>
          It is difficult to plan and execute youth programs on your own, invite your team to start
          planning today.
        </p>
        <div>
          <ProgressDots
            currentStep={DefaultOnboarding.length - onboarding.length}
            totalSteps={Math.max(DefaultOnboarding.length, onboarding.length)}
          />
        </div>
        <NavLink to={ROUTES.TEAM_INVITE} className="btn btn-primary">
          Invite teammates
        </NavLink>
        <button type="button" className="btn ms-2" onClick={() => handleSkip('team')}>
          Skip
        </button>
      </>
    )
    content.image = <UndrawSelectingTeam className="img-fluid" />
  } else if (onboarding.includes('payout')) {
    content.title = <>Let's setup your online payments</>
    content.text = (
      <>
        <p>
          In order to get paid for your hard efforts, we need you to first setup your online payment
          settings.
        </p>
        <div>
          <ProgressDots
            currentStep={DefaultOnboarding.length - onboarding.length}
            totalSteps={Math.max(DefaultOnboarding.length, onboarding.length)}
          />
        </div>
        <button type="button" className="btn btn-primary" onClick={() => handleSetupPayout()}>
          Setup payments
        </button>
      </>
    )
    content.image = <UndrawPayOnline className="img-fluid" />
  } else if (isRecommendedToUpgradePlan) {
    content.title = <>Let's manage your subscription</>
    content.text = (
      <>
        <p>Gain access to advanced features and our support team by upgrading your billing plan.</p>
        <NavLink to={ROUTES.BILLING_SETTINGS} className="btn btn-primary">
          Manage Billing
        </NavLink>
        <button type="button" className="btn ms-2" onClick={() => handleSkip('billing')}>
          Skip
        </button>
      </>
    )
    content.image = <UndrawReviewedDocs className="img-fluid" />
  } else if (isRecommendedToReferral) {
    content.title = <>Get {referalAmount} off</>
    content.text = (
      <>
        <p>
          <b>Give the gift of Citrus to other programs.</b> You both get a promo when your friend
          makes their first payment using Citrus.
        </p>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => {
            setIsReferralModalOpen(true)
          }}
        >
          See details
        </button>
        <button type="button" className="btn ms-2" onClick={() => handleSkip('referral')}>
          Skip
        </button>
        {isReferralModalOpen && program && (
          <ReferralModal program={program} onClose={() => setIsReferralModalOpen(false)} />
        )}
      </>
    )
    content.image = <UndrawPayOnline className="img-fluid" />
  } else {
    // // Handle date related messages
    // const isWeekend = today.toFormat('ccc') === 'Sat' || today.toFormat('ccc') === 'Sun'
    // const isEvening = today.hour >= 21 || today.hour <= 3
    // const isMorning = today.hour >= 4 && today.hour < 12
    // const isHalloween = today.day === 31 && today.month === 10
    // const isHolidays =
    //   (today.day > 17 && today.month === 12) || (today.day < 1 && today.month === 1)
    // if (isHalloween) {
    //   content.title = <>Happy Halloween!</>
    //   content.text = <>We hope that you are having both a spooky and productive day.</>
    //   content.image = <UndrawHalloween className="img-fluid" />
    // } else if (isHolidays) {
    //   content.title = <>Merry Christmas!</>
    //   content.text = (
    //     <>
    //       We hope that you are enjoying the Christmas season and wish you and your family a joyful
    //       and blessed holiday filled with happiness.
    //     </>
    //   )
    //   content.image = <UndrawSnowGames className="img-fluid" />
    // } else if (isWeekend) {
    //   content.title = <>The grind never stops {user.first_name}!</>
    //   content.text = (
    //     <>
    //       Every minute you invest this weekend gets your business closer to success. Keep grinding!
    //     </>
    //   )
    // } else if (isEvening) {
    //   content.title = <>Good evening {user.first_name}!</>
    //   content.text = <>The grind after hours defines the best. Keep pushing.</>
    //   content.image = <UndrawResearching className="img-fluid" />
    // } else if (isMorning) {
    //   content.title = <>Good morning {user.first_name}!</>
    // }
  }

  if (isLoading) {
    return <div className="db-header animate py-5"></div>
  }

  return (
    <div className="rounded rounded-3 p-4 bg-body-tertiary border border-body-secondary">
      <h3 className="mb-3 text-body">{content.title}</h3>
      <div>{content.text}</div>
    </div>
  )
}
